import { useEffect, useState } from "react";

import { WorkflowOrganisationAccessLevel } from "../../constants";
import { SelectData } from "../../models";
import { GetTaskBoardResponse } from "../../service/query";
import { hasWorkflowOrganisationAccessLevel } from "../../utils";
import { TaskPanelMode } from "./taskPanelMode";

interface UseTaskAccessProps {
  mode: TaskPanelMode;
  isGeneralTask: boolean;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  currentOrganisationUuid: string | null | undefined;
  // Whether the acting org holds manageTask on the record (from the task's own ACL).
  generalTaskCanEdit: boolean;
}

interface UseTaskAccessReturnData {
  // The workflow's organisations, for the Owner organisation picker.
  organisationsSelectData: SelectData;
  hasFullEditAccess: boolean;
  /*
    The access level the right-rail gates read. A general task has no workflow to carry one, so one is
    synthesised from its per-record grant — Author when it may be edited, Viewer when it may not.
  */
  effectiveAccessLevel: WorkflowOrganisationAccessLevel | undefined;
  resetAccess: () => void;
}

/*
  Who may edit the open task. The two task kinds answer it from different places: a workflow task from its
  organisation's access level on the workflow, a general task from the per-record manageTask grant.
*/
export const useTaskAccess = ({
  mode,
  isGeneralTask,
  workflow,
  currentOrganisationUuid,
  generalTaskCanEdit,
}: UseTaskAccessProps): UseTaskAccessReturnData => {
  const [organisationsSelectData, setOrganisationsSelectData] = useState<SelectData>([]);
  const [workflowOrganisationAccessLevel, setWorkflowOrganisationAccessLevel] =
    useState<WorkflowOrganisationAccessLevel>();
  const [createdByCurrentUserOrg, setCreatedByCurrentUserOrg] = useState(false);

  const createdByOrganisationUuid = mode.kind === "edit" ? mode.task.createdByOrganisation.uuid : undefined;

  useEffect(() => {
    setCreatedByCurrentUserOrg(createdByOrganisationUuid === currentOrganisationUuid);
  }, [createdByOrganisationUuid, currentOrganisationUuid]);

  useEffect(() => {
    if (workflow?.organisations && workflow.organisations.length > 0) {
      setOrganisationsSelectData(
        workflow.organisations.map((organisation) => ({
          key: organisation.organisationUuid,
          value: organisation.name,
        })) as SelectData
      );
      setWorkflowOrganisationAccessLevel(
        workflow.organisations.find((x) => x.organisationUuid === currentOrganisationUuid)
          ?.accessLevel as WorkflowOrganisationAccessLevel
      );
    }
  }, [workflow, currentOrganisationUuid]);

  // Creating is always editable — there is no record to be denied yet.
  const workflowHasFullEditAccess =
    mode.kind === "create" ||
    hasWorkflowOrganisationAccessLevel(WorkflowOrganisationAccessLevel.Author, workflowOrganisationAccessLevel) ||
    (hasWorkflowOrganisationAccessLevel(WorkflowOrganisationAccessLevel.Contributor, workflowOrganisationAccessLevel) &&
      createdByCurrentUserOrg);

  const generalCanEdit = mode.kind === "edit" ? generalTaskCanEdit : true;
  const hasFullEditAccess = isGeneralTask ? generalCanEdit : workflowHasFullEditAccess;

  const generalTaskAccessLevel = generalCanEdit
    ? WorkflowOrganisationAccessLevel.Author
    : WorkflowOrganisationAccessLevel.Viewer;

  return {
    organisationsSelectData,
    hasFullEditAccess,
    effectiveAccessLevel: isGeneralTask ? generalTaskAccessLevel : workflowOrganisationAccessLevel,
    resetAccess: () => setCreatedByCurrentUserOrg(false),
  };
};
